/**
 * SubscriptionUpgradeModal - Tier upgrade modal
 *
 * MDM-UNIFIED-001 Phase 1: Tier Enforcement & Pricing
 *
 * Shows subscription tiers, pricing, and handles Stripe checkout redirect.
 * Per constitution_ui.json §6: "Every action returns feedback state"
 */
import React, { useState, useEffect, memo } from 'react';
import {
  X,
  Crown,
  Check,
  Loader2,
  CreditCard,
  Zap,
  Shield,
  Star,
  AlertCircle,
} from 'lucide-react';
import { API_BASE, getStoredToken } from '../../services/api';

const SubscriptionUpgradeModal = memo(function SubscriptionUpgradeModal({
  isOpen,
  onClose,
  currentTier = 'free',
  onUpgradeSuccess,
}) {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricing, setPricing] = useState(null);

  // Fetch pricing info on mount
  useEffect(() => {
    if (isOpen) {
      fetchPricing();
    }
  }, [isOpen]);

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_BASE}/subscriptions/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const handleUpgrade = async (tier) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = getStoredToken();
      const response = await fetch(`${API_BASE}/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          tier,
          billing_cycle: billingCycle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Default pricing if API fails
  const defaultPricing = pricing?.tiers || {
    free: {
      name: 'Free',
      price_monthly: 0,
      price_yearly: 0,
      features: [
        '3 AI grading predictions per month',
        '50 collection items',
        'Basic comic identification',
        'Basic convention listings',
      ],
    },
    collector: {
      name: 'Collector',
      price_monthly: 9.99,
      price_yearly: 99,
      features: [
        'Unlimited AI grading predictions',
        '500 collection items',
        'Full convention intelligence',
        'Signature tracking',
        'Price alerts & badges',
        'Priority support',
      ],
      popular: true,
    },
    intelligence: {
      name: 'Intelligence',
      status: 'coming_soon',
      price_monthly: 19.99,
      price_yearly: 199,
      features: [
        'Everything in Collector',
        'Unlimited collection items',
        'Signature opportunity alerts',
        'Predictive recommendations',
        'API access',
      ],
    },
  };

  const tiers = defaultPricing;
  const yearlyDiscount = Math.round(
    ((tiers.collector.price_monthly * 12 - tiers.collector.price_yearly) /
      (tiers.collector.price_monthly * 12)) *
      100
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Upgrade Your Experience
                </h2>
                <p className="text-sm text-zinc-400">
                  Unlock premium features and supercharge your collection
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-orange-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-orange-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                Save {yearlyDiscount}%
              </span>
            </button>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Pricing cards */}
        <div className="p-6 grid md:grid-cols-3 gap-4">
          {/* Free tier */}
          <div
            className={`rounded-xl border p-6 ${
              currentTier === 'free'
                ? 'border-zinc-600 bg-zinc-800/50'
                : 'border-zinc-700'
            }`}
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">
                {tiers.free.name}
              </h3>
              <div className="mt-2">
                <span className="text-3xl font-bold text-white">$0</span>
                <span className="text-zinc-400">/forever</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {tiers.free.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-400">{feature}</span>
                </li>
              ))}
            </ul>

            {currentTier === 'free' && (
              <div className="py-2 px-4 bg-zinc-700 rounded-lg text-center text-zinc-300 text-sm">
                Current Plan
              </div>
            )}
          </div>

          {/* Collector tier */}
          <div
            className={`rounded-xl border-2 p-6 relative ${
              currentTier === 'collector'
                ? 'border-orange-500/50 bg-orange-500/5'
                : 'border-orange-500'
            }`}
          >
            {tiers.collector.popular && currentTier !== 'collector' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
                Most Popular
              </div>
            )}

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5 text-orange-500" />
                <h3 className="text-lg font-semibold text-white">
                  {tiers.collector.name}
                </h3>
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold text-white">
                  $
                  {billingCycle === 'yearly'
                    ? Math.round(tiers.collector.price_yearly / 12)
                    : tiers.collector.price_monthly}
                </span>
                <span className="text-zinc-400">/month</span>
                {billingCycle === 'yearly' && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Billed ${tiers.collector.price_yearly}/year
                  </p>
                )}
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {tiers.collector.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-white">{feature}</span>
                </li>
              ))}
            </ul>

            {currentTier === 'collector' ? (
              <div className="py-2 px-4 bg-orange-500/20 border border-orange-500/30 rounded-lg text-center text-orange-400 text-sm">
                Current Plan
              </div>
            ) : (
              <button
                onClick={() => handleUpgrade('collector')}
                disabled={isLoading}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Upgrade Now
                  </>
                )}
              </button>
            )}
          </div>

          {/* Intelligence tier (coming soon) */}
          <div className="rounded-xl border border-zinc-700 p-6 opacity-60">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-purple-500" />
                <h3 className="text-lg font-semibold text-white">
                  {tiers.intelligence.name}
                </h3>
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold text-white">
                  $
                  {billingCycle === 'yearly'
                    ? Math.round(tiers.intelligence.price_yearly / 12)
                    : tiers.intelligence.price_monthly}
                </span>
                <span className="text-zinc-400">/month</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {tiers.intelligence.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Star className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-400">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="py-2 px-4 bg-purple-500/10 border border-purple-500/20 rounded-lg text-center text-purple-400 text-sm">
              Coming Soon
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 p-4 text-center">
          <p className="text-zinc-500 text-sm">
            Cancel anytime. No long-term contracts.
          </p>
        </div>
      </div>
    </div>
  );
});

export default SubscriptionUpgradeModal;
